import { AuthProvider } from '@griply/types';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { FormInput } from 'shared/components/ui/form-input';

import * as Styled from './delete-account.style';

export type DeleteAccountReauthProps = {
  provider: AuthProvider | null;
  password: string;
  onChangePassword: (value: string) => void;
  errorMessage?: string;
  isSubmitting: boolean;
  onSubmitPassword: () => void;
  onContinueSocial: () => void;
  onCancel: () => void;
};

export const DeleteAccountReauth: React.FC<DeleteAccountReauthProps> = ({
  provider,
  password,
  onChangePassword,
  errorMessage,
  isSubmitting,
  onSubmitPassword,
  onContinueSocial,
  onCancel,
}) => {
  const { t } = useTranslation();
  const isPassword = provider === AuthProvider.Email;
  const providerName = provider === AuthProvider.Apple ? 'Apple' : 'Google';

  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangePassword(event.target.value);
  };

  return (
    <>
      <Styled.Header>
        <Typography variant="h5" component="h1">
          {t('dialogs.delete-account.reauth.title')}
        </Typography>
      </Styled.Header>

      <Styled.Body>
        {isPassword ? (
          <>
            <Typography variant="body2">
              {t('dialogs.delete-account.reauth.message')}
            </Typography>
            <Styled.Field>
              <FormInput
                id="delete-account-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={handleChangePassword}
                placeholder={t(
                  'dialogs.delete-account.reauth.password.placeholder',
                )}
                error={errorMessage}
                disabled={isSubmitting}
              />
              {errorMessage ? (
                <Styled.ErrorMessage>{errorMessage}</Styled.ErrorMessage>
              ) : null}
            </Styled.Field>
          </>
        ) : (
          <Typography variant="body2">
            {t('dialogs.delete-account.reauth.redirect.message', {
              provider: providerName,
            })}
          </Typography>
        )}
      </Styled.Body>

      <Styled.Interactions>
        <Button
          variant={Variants.Outlined}
          size={Sizes.Medium}
          color={Colors.Inherit}
          onClick={onCancel}
        >
          {t('dialogs.delete-account.reauth.button.cancel')}
        </Button>
        {isPassword ? (
          <Button
            size={Sizes.Medium}
            color={Colors.Primary}
            onClick={onSubmitPassword}
            disabled={isSubmitting || !password}
          >
            {t('dialogs.delete-account.reauth.button.submit')}
          </Button>
        ) : (
          <Button
            size={Sizes.Medium}
            color={Colors.Primary}
            onClick={onContinueSocial}
          >
            {t('dialogs.delete-account.reauth.redirect.button', {
              provider: providerName,
            })}
          </Button>
        )}
      </Styled.Interactions>
    </>
  );
};
